/**
 * CatalogImportDialog
 * Adds dishes from the TPV catalog without creating fake orders (spec 010-menu-platos-tpv).
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AlertCircle, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { AdminButton } from '@/components/admin/shell/AdminButton';
import { itemService } from '@/services/itemService';
import { filterCatalog } from '@/lib/catalogFilter';
import { formatPrice } from '@/lib/demoPrice';
import type { CatalogImportResponse } from '@/types/item';

interface CatalogImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported?: (result: CatalogImportResponse) => void;
}

function apiErrorMessage(error: unknown, fallback: string): string {
  const apiError = error as { response?: { data?: { error?: string } } };
  return apiError?.response?.data?.error || fallback;
}

function formatDate(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function CatalogImportDialog({ open, onOpenChange, onImported }: CatalogImportDialogProps) {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin-items-catalog'],
    queryFn: () => itemService.getCatalog(),
    enabled: open,
    staleTime: 60_000,
    retry: false,
  });

  const knownCategories = useMemo(() => data?.known_categories ?? [], [data]);
  const allItems = useMemo(() => data?.items ?? [], [data]);
  const visibleItems = useMemo(
    () => filterCatalog(allItems, { query, showAll, knownCategories }),
    [allItems, query, showAll, knownCategories]
  );
  const canToggle = knownCategories.length > 0 && !query.trim();

  const importMutation = useMutation({
    mutationFn: (ids: string[]) => itemService.importFromCatalog(ids),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['admin-items'] });
      queryClient.invalidateQueries({ queryKey: ['admin-items-catalog'] });
      const count = result.created.length;
      toast.success(
        count === 1
          ? 'Plato añadido. Ya puedes subir su foto.'
          : `${count} platos añadidos. Ya puedes subir sus fotos.`
      );
      onImported?.(result);
      handleOpenChange(false);
    },
    onError: (err: unknown) => {
      toast.error(apiErrorMessage(err, 'No se han podido añadir los platos'));
    },
  });

  function handleOpenChange(next: boolean) {
    if (!next) {
      setQuery('');
      setShowAll(false);
      setSelected(new Set());
    }
    onOpenChange(next);
  }

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl h-[100dvh] sm:h-auto sm:max-h-[90vh] flex flex-col gap-4 p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="text-[20px] font-light text-[#1e1e1e] uppercase tracking-[1.5px]">
            Añadir platos del TPV
          </DialogTitle>
          <DialogDescription className="text-[14px] font-light text-[#9d9c9c]">
            Productos de tu carta del TPV que aún no están en Kombu. Elige los que quieras y después
            completa su foto y descripción.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-content-secondary" />
          <Input
            placeholder="Buscar en toda la carta…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="min-h-11 pl-10 border-content-primary focus-visible:ring-content-primary font-light"
            aria-label="Buscar en la carta del TPV"
          />
        </div>

        {canToggle && (
          <div className="flex flex-wrap items-center justify-between gap-2 text-[13px] font-light text-content-secondary">
            <span>
              {showAll
                ? 'Mostrando toda la carta, también bebidas y vinos.'
                : `Mostrando las categorías del TPV donde ya tienes platos: ${knownCategories.join(', ')}.`}
            </span>
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="min-h-11 px-2 underline text-content-primary"
            >
              {showAll ? 'Ver solo esas categorías' : 'Ver toda la carta'}
            </button>
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto border border-gray-200 divide-y divide-gray-100">
          {isLoading && (
            <p className="p-6 text-center text-sm font-light text-content-secondary">Leyendo la carta del TPV…</p>
          )}

          {isError && (
            <div className="p-6 text-center space-y-3">
              <p className="flex items-center justify-center gap-2 text-sm text-red-600">
                <AlertCircle className="h-4 w-4" />
                {apiErrorMessage(error, 'No se ha podido leer la carta del TPV')}
              </p>
              <AdminButton size="sm" onClick={() => refetch()}>
                Reintentar
              </AdminButton>
            </div>
          )}

          {data && visibleItems.length === 0 && (
            <p className="p-6 text-center text-sm font-light text-content-secondary">
              {allItems.length === 0
                ? 'Todos los productos de tu carta del TPV ya están en Kombu.'
                : 'No hay productos que coincidan.'}
            </p>
          )}

          {visibleItems.map((item) => {
            const checked = selected.has(item.remote_item_id);
            const created = formatDate(item.created_at);
            return (
              <label
                key={item.remote_item_id}
                className={`flex items-start gap-3 px-4 py-3 min-h-11 cursor-pointer transition-colors ${
                  checked ? 'bg-gray-50' : 'hover:bg-gray-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(item.remote_item_id)}
                  className="mt-1 h-5 w-5 shrink-0 accent-[#1e1e1e]"
                  aria-label={item.name}
                />
                <span className="flex-1 min-w-0">
                  <span className="block text-[14px] font-light text-[#1e1e1e]">{item.name}</span>
                  <span className="block text-[11px] font-light text-[#9d9c9c] uppercase tracking-wider">
                    {[item.square_categories.join(' · ') || 'Sin categoría en el TPV', created && `Creado el ${created}`]
                      .filter(Boolean)
                      .join(' — ')}
                  </span>
                  {item.name_in_kombu && (
                    <span className="block text-[11px] font-medium text-orange-600">
                      Ya tienes un plato con este nombre en Kombu
                    </span>
                  )}
                  {item.suggested_category && (
                    <span className="block text-[11px] font-light text-content-secondary">
                      Irá a {item.suggested_category}
                    </span>
                  )}
                </span>
                {item.price_cents ? (
                  <span className="text-[14px] font-light text-[#1e1e1e] whitespace-nowrap">
                    {formatPrice(item.price_cents, item.currency)}
                  </span>
                ) : null}
              </label>
            );
          })}
        </div>

        <DialogFooter className="flex-row gap-2 sm:justify-between items-center">
          <span className="text-[13px] font-light text-content-secondary mr-auto">
            {selected.size === 0 ? 'Ningún plato elegido' : `${selected.size} elegidos`}
          </span>
          <AdminButton className="min-h-11" onClick={() => handleOpenChange(false)}>
            Cancelar
          </AdminButton>
          <AdminButton
            variant="primary"
            className="min-h-11"
            disabled={selected.size === 0}
            isLoading={importMutation.isPending}
            onClick={() => importMutation.mutate(Array.from(selected))}
          >
            Añadir{selected.size > 0 ? ` (${selected.size})` : ''}
          </AdminButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
